/**
 * Landlord Onboarding Workflow Component
 * Tracks individual landlords through the 5-stage white-glove onboarding process
 * Similar to customer onboarding but for individual landlords
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Flex,
  Text,
  Badge,
  // Progress,
  // Button,
  useToast,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  // StatHelpText,
  Icon,
  VStack,
  // HStack,
  // Divider,
} from '@chakra-ui/react';
import {
  MdOutlinePersonSearch,
  MdOutlinePersonAdd,
  MdOutlineEmail,
  MdOutlineLogin,
  MdOutlineCheckCircle,
} from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import LandlordOnboardingList from './LandlordOnboardingList';
import LandlordOnboardingDetail from './LandlordOnboardingDetail';
import LandlordOnboardingMetrics from './LandlordOnboardingMetrics';
import type {
  LandlordOnboarding,
  LandlordOnboardingStats,
  LandlordOnboardingStatus,
} from '../../../types/landlordOnboarding';

const API_BASE = 'https://us-central1-un-dollar-rentflow-dev.cloudfunctions.net';

/**
 * Landlord Onboarding Workflow
 */
const LandlordOnboardingWorkflow: React.FC = () => {
  const { t } = useTranslation('landlordOnboarding');
  const toast = useToast();

  const [activeTab, setActiveTab] = useState(0);
  const [selectedOnboarding, setSelectedOnboarding] = useState<LandlordOnboarding | null>(null);
  const [onboardings, setOnboardings] = useState<LandlordOnboarding[]>([]);
  const [stats, setStats] = useState<LandlordOnboardingStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<LandlordOnboardingStatus | 'all'>('all');

  // Load onboardings
  useEffect(() => {
    loadOnboardings();
    loadStats();
  }, [filter]);

  const loadOnboardings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.append('status', filter);
      }
      
      const response = await fetch(
        `${API_BASE}/getLandlordOnboardings?${params.toString()}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to load onboardings');
      }
      
      const data = await response.json();
      setOnboardings(data.onboardings || []);
    } catch (error) {
      console.error('Error loading onboardings:', error);
      toast({
        title: t('loadError'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setOnboardings([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/getLandlordOnboardingStats`);
      if (!response.ok) {
        throw new Error('Failed to load stats');
      }
      const data = await response.json();
      setStats(data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats(null);
    }
  };

  const handleSelectOnboarding = (onboarding: LandlordOnboarding) => {
    setSelectedOnboarding(onboarding);
    setActiveTab(1); // Switch to detail tab
  };

  const handleBackToList = () => {
    setSelectedOnboarding(null);
    setActiveTab(0);
    loadOnboardings(); // Refresh list
  };

  // Quick stats
  const quickStats = [
    {
      label: t('stats.active'),
      value: stats?.activeOnboardings || 0,
      icon: MdOutlinePersonSearch,
      color: 'blue',
    },
    {
      label: t('stats.completedThisMonth'),
      value: stats?.completedThisMonth || 0,
      icon: MdOutlineCheckCircle,
      color: 'green',
    },
    {
      label: t('stats.avgTimeToLogin'),
      value: stats?.avgTimeToFirstLogin
        ? `${Math.round(stats.avgTimeToFirstLogin)}h`
        : 'N/A',
      icon: MdOutlineLogin,
      color: 'purple',
    },
    {
      label: t('stats.gmailConnectionRate'),
      value: stats?.gmailConnectionRate48h
        ? `${Math.round(stats.gmailConnectionRate48h)}%`
        : 'N/A',
      icon: MdOutlineEmail,
      color: 'orange',
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">{t('title')}</Heading>
          <Text color="gray.600" fontSize="sm">
            {t('subtitle')}
          </Text>
        </VStack>
      </Flex>

      {/* Quick Stats */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        {quickStats.map((stat, index) => (
          <Card key={index}>
            <CardBody>
              <Stat>
                <Flex justify="space-between" align="start">
                  <Box>
                    <StatLabel fontSize="sm" color="gray.600">
                      {stat.label}
                    </StatLabel>
                    <StatNumber fontSize="2xl" mt={1}>
                      {stat.value}
                    </StatNumber>
                  </Box>
                  <Icon
                    as={stat.icon}
                    boxSize={6}
                    color={`${stat.color}.500`}
                  />
                </Flex>
              </Stat>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {/* Main Content */}
      <Card>
        <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue">
          <CardHeader>
            <TabList>
              <Tab>
                <Icon as={MdOutlinePersonSearch} mr={2} />
                {t('tabs.list')}
                {stats && stats.activeOnboardings > 0 && (
                  <Badge ml={2} colorScheme="blue">
                    {stats.activeOnboardings}
                  </Badge>
                )}
              </Tab>
              <Tab isDisabled={!selectedOnboarding}>
                <Icon as={MdOutlinePersonAdd} mr={2} />
                {t('tabs.detail')}
              </Tab>
              <Tab>
                <Icon as={MdOutlineCheckCircle} mr={2} />
                {t('tabs.metrics')}
              </Tab>
            </TabList>
          </CardHeader>

          <CardBody>
            <TabPanels>
            {/* Onboarding List */}
            <TabPanel p={0}>
              <LandlordOnboardingList
                onboardings={onboardings}
                loading={loading}
                filter={filter}
                onFilterChange={setFilter}
                onSelectOnboarding={handleSelectOnboarding}
                onRefresh={loadOnboardings}
              />
            </TabPanel>

            {/* Onboarding Detail */}
            <TabPanel p={0}>
              {selectedOnboarding ? (
                <LandlordOnboardingDetail
                  onboarding={selectedOnboarding}
                  onBack={handleBackToList}
                  onUpdate={loadOnboardings}
                />
              ) : (
                <Flex justify="center" align="center" minH="300px">
                  <Text color="gray.500">
                    {t('selectOnboarding')}
                  </Text>
                </Flex>
              )}
            </TabPanel>

            {/* Metrics */}
            <TabPanel p={0}>
              <LandlordOnboardingMetrics stats={stats} />
            </TabPanel>
          </TabPanels>
        </CardBody>
      </Tabs>
      </Card>
    </Box>
  );
};

export default LandlordOnboardingWorkflow;
